import { CallIntelTypes } from '@frontend/api-call-intel';
import { SchedulingChip } from '@frontend/call-intel-core';
import { i18next } from '@frontend/i18n';
import { CallIntelLocationComparisonStackChart } from '../charts/call-intel-location-comparison-stack-chart';
import { OpportunityConversionLeaderboardV2 } from '../charts/opportunity-conversion-leaderboard';
import {
  ChartConfigItem,
  FormattedChartData,
  InferencesChartData,
  SchedulingFormattedData,
  SummaryCardClickHandler,
} from './types';

type DataLabels = {
  appointmentTypes?: Record<string, string>;
  categories?: Record<string, string>;
  sentiments?: Record<string, string>;
};

type DataColors = {
  schedulingOutcomeV2?: Record<string, string>;
};

type GetChartConfigParams = {
  dataColors: DataColors;
  dataLabels: DataLabels;
  drillDownKey: string;
  drillDownValue: string;
  internalPage?: boolean;
  onClickSummaryCard?: SummaryCardClickHandler;
  opportunitiesChartTitle: string;
};

export function getChartConfig({
  dataColors,
  dataLabels,
  drillDownKey,
  drillDownValue,
  internalPage,
  onClickSummaryCard,
  opportunitiesChartTitle,
}: GetChartConfigParams): ChartConfigItem[] {
  return [
    {
      id: 'schedulingStats' as const,
      shouldRender: true,
      renderChart: (data: FormattedChartData, isLoading: boolean) => {
        const schedulingData = data as SchedulingFormattedData;
        return internalPage ? (
          <CallIntelLocationComparisonStackChart
            categorisedComparison
            colors={{
              scheduledOnCallCount:
                dataColors.schedulingOutcomeV2?.[
                  CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL
                ] ?? '',
              unscheduledOnCall:
                dataColors.schedulingOutcomeV2?.[
                  CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED
                ] ?? '',
              scheduledPostCallCount:
                dataColors.schedulingOutcomeV2?.[
                  CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL
                ] ?? '',
            }}
            data={schedulingData.opportunities}
            internalPage
            isLoading={isLoading}
            labels={{
              scheduledOnCallCount: (
                <SchedulingChip
                  schedulingOutcome={CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL}
                  showTooltip={false}
                />
              ),
              unscheduledOnCall: (
                <SchedulingChip
                  schedulingOutcome={CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED}
                  showTooltip={false}
                />
              ),
              scheduledPostCallCount: (
                <SchedulingChip
                  schedulingOutcome={CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL}
                  showTooltip={false}
                />
              ),
            }}
            title={opportunitiesChartTitle}
          />
        ) : (
          <OpportunityConversionLeaderboardV2
            categorisedComparison={false}
            data={schedulingData.leaderboard}
            isLoading={isLoading}
            onClick={onClickSummaryCard}
            title={opportunitiesChartTitle}
          />
        );
      },
    },
    {
      id: 'sentiments' as const,
      shouldRender: drillDownKey !== 'sentiments',
      renderChart: (data: FormattedChartData, isLoading: boolean) => (
        <CallIntelLocationComparisonStackChart
          categorisedComparison
          data={data as InferencesChartData}
          dataType='sentiment'
          isLoading={isLoading}
          internalPage={internalPage}
          title={getDrillDownTitle({
            drillDownKey,
            drillDownValue,
            dataLabels,
            baseTitle: i18next.t('Customer Sentiment', { ns: 'call-intelligence' }),
            drillDownSuffix: i18next.t('Customer Sentiment', { ns: 'call-intelligence' }),
            excludeKey: 'sentiments',
          })}
        />
      ),
    },
    {
      id: 'categories' as const,
      shouldRender: drillDownKey !== 'categories',
      renderChart: (data: FormattedChartData, isLoading: boolean) => (
        <CallIntelLocationComparisonStackChart
          categorisedComparison
          data={data as InferencesChartData}
          dataType='category'
          isLoading={isLoading}
          internalPage={internalPage}
          title={getDrillDownTitle({
            drillDownKey,
            drillDownValue,
            dataLabels,
            baseTitle: i18next.t('Calls by Category', { ns: 'call-intelligence' }),
            drillDownSuffix: i18next.t('Call Categories', { ns: 'call-intelligence' }),
            excludeKey: 'categories',
          })}
        />
      ),
    },
    {
      id: 'appointmentTypes' as const,
      shouldRender: drillDownKey !== 'appointment_types',
      renderChart: (data: FormattedChartData, isLoading: boolean) => (
        <CallIntelLocationComparisonStackChart
          categorisedComparison
          data={data as InferencesChartData}
          dataType='appointment-type'
          isLoading={isLoading}
          internalPage={internalPage}
          title={getDrillDownTitle({
            drillDownKey,
            drillDownValue,
            dataLabels,
            baseTitle: i18next.t('Calls by Appointment Type', { ns: 'call-intelligence' }),
            drillDownSuffix: i18next.t('Appointment Types', { ns: 'call-intelligence' }),
            excludeKey: 'appointment_types',
          })}
        />
      ),
    },
  ];
}

// --- Helper to compute drill-down chart titles ---

type DrillDownTitleParams = {
  drillDownKey: string;
  drillDownValue: string;
  dataLabels: DataLabels;
  baseTitle: string;
  drillDownSuffix: string;
  excludeKey: string;
};

function getDrillDownTitle({
  drillDownKey,
  drillDownValue,
  dataLabels,
  baseTitle,
  drillDownSuffix,
  excludeKey,
}: DrillDownTitleParams): string {
  if (!drillDownKey) return baseTitle;

  const label =
    drillDownKey === 'appointment_types'
      ? dataLabels.appointmentTypes?.[drillDownValue]
      : drillDownKey === 'categories'
      ? dataLabels.categories?.[drillDownValue]
      : drillDownKey === excludeKey
      ? undefined
      : dataLabels.sentiments?.[drillDownValue];

  return i18next.t(`{{value}} Calls - ${drillDownSuffix}`, { value: label, ns: 'call-intelligence' });
}
